import { validateRegionalTaxId, VALIDATION_KEYS } from "@fysaro/shared";
import { z } from "zod";

// Para crear desde el registro inicial (Onboarding Step 1)
export const createClinicWithOwnerSchema = z.object({
  clinic: z.object({
    name: z.string().min(2),
    address: z.string().optional(),
    isMain: z.boolean().default(true), // La primera siempre es principal
  }),
  owner: z.object({
    email: z.string().email(),
    password: z.string().min(8),
    name: z.string(),
  }),
});

// El esquema CORE para CRUD (Crear/Editar)
// Quitamos street y housenumber porque ya viven en 'address' o 'formattedAddress'
export const clinicBodySchema = z
  .object({
    name: z.string().min(2, VALIDATION_KEYS.CLINIC.NAME_REQUIRED),
    slug: z.string().min(2, VALIDATION_KEYS.CLINIC.SLUG_REQUIRED),

    // Contacto (Opcionales pero validados si se escriben)
    email: z
      .string()
      .email(VALIDATION_KEYS.GENERIC.INVALID_EMAIL)
      .optional()
      .nullable()
      .or(z.literal("")),
    phone: z.string().optional().nullable().or(z.literal("")),

    // Dirección
    address: z.string().min(5, VALIDATION_KEYS.CLINIC.ADDRESS_REQUIRED),
    street: z.string().optional(),
    housenumber: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().optional(),

    country: z.string().optional().default("Spain"),
    countryCode: z.string().optional().default("ES"),

    currency: z.string().optional().default("EUR"),
    language: z.string().optional().default("es"),

    taxId: z.string().default(""),
    taxIdType: z.string().optional().or(z.literal("")),

    // Geo
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    formattedAddress: z.string().optional(),

    isMain: z.boolean().default(false),
    status: z.enum(["ACTIVE", "INACTIVE", "ARCHIVED"]).default("ACTIVE"),
    imageUrl: z.string().url().optional().nullable(),
    totalInvoices: z.number().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    const country = data.countryCode || "ES";
    // Limpiamos espacios
    const taxId = data.taxId?.trim() || "";

    // 1ï¸âƒ£ CASO: El campo está completamente vací­o o tiene menos de 3 letras (Obligatorio)
    if (taxId.length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["taxId"],
        message: VALIDATION_KEYS.CLINIC.TAX_ID_REQUIRED, // O tu clave de traducción
      });
      return; // Frenamos aquí­, ya sabemos que está mal por tamaño
    }

    // 2ï¸âƒ£ CASO: El usuario está escribiendo pero aún no ha completado una longitud madura regional
    // (Evitamos escupir errores de algoritmo "DNI Inválido" mientras teclea los primeros números)
    if (taxId.length < 6) return;

    // 3ï¸âƒ£ CASO: Validación algorí­tmica real usando tu librerí­a shared
    // Â¡Ahora sí­ va a entrar siempre que tenga 6 o más caracteres!
    if (!validateRegionalTaxId(country, taxId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["taxId"],
        message: VALIDATION_KEYS.CLINIC.INVALID_TAX_ID,
      });
    }
  });

// Especí­fico para creación (Añadimos tenantId)
export const createClinicSchema = clinicBodySchema.extend({
  tenantId: z.string().uuid("ID de Tenant inválido").optional(),
});

// Creación desde panel superadmin (owner opcional)
export const createSuperadminClinicSchema = createClinicSchema.extend({
  ownerId: z.string().uuid(),
});

// Especí­fico para update (Hacemos los campos opcionales pero estrictos)
export const updateClinicSchema = clinicBodySchema;

// Traspaso de clí­nica entre organizaciones (Tenants)
export const reassignTenantSchema = z.object({
  tenantId: z.string().uuid(),
});

export const updateClinicImageSchema = z.object({
  imageUrl: z.string().url().nullable(),
  field: z.enum(["logoUrl", "faviconUrl"]).optional(),
});

export const clinicUuidSchema = z.object({
  id: z.string().uuid(),
});

// Búsqueda: Ahora filtramos por tenantId obligatoriamente en la mayorí­a de casos
export const searchClinicsSchema = z.object({
  page: z.coerce.number().default(1),
  pageSize: z.coerce.number().default(10),
  search: z.string().optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "ALL"]).optional(),
  tenantId: z.string().uuid().optional(), // Para que SuperAdmin pueda ver todas las de un Tenant
});
